import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
    Alert, 
    Button, 
    TextField, 
    Checkbox, 
    FormControlLabel, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    Box,
    Typography,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDAlert from "../../components/MDAlert";
import MDTypography from "../../components/MDTypography";
import { AuthContext } from "../../context";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";
import toast from "react-hot-toast";

function RoleAdd() {
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    
    const [roleData, setRoleData] = useState({
        name: '',
        description: ''
    });
    const [selectedAuths, setSelectedAuths] = useState([]);
    const [availableAuths, setAvailableAuths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchAuthorizations = async () => {
            try {
                const authsResponse = await apiService.get("/authorizations/", {}, {});
                setAvailableAuths(authsResponse);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching authorizations:", error);
                setError("Errore durante il caricamento delle autorizzazioni");
                setLoading(false);
            }
        };

        fetchAuthorizations();
    }, []);

    const handleInputChange = (field, value) => {
        setRoleData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAuthToggle = (authId) => {
        setSelectedAuths(prev => {
            if (prev.includes(authId)) {
                return prev.filter(id => id !== authId);
            } else {
                return [...prev, authId];
            }
        });
    };

    const handleSave = async () => {
        if (!roleData.name || !roleData.description) {
            setError("Nome e Descrizione sono obbligatori");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: roleData.name,
                description: roleData.description,
                auths: selectedAuths,
                is_yard_responsable: false,
                is_security_responsable: false,
                is_supplier_responsable: false,
                is_dl: false,
                is_site_manager_technician: false
            };

            const response = await apiService.post("/role-management/", {}, payload);
            toast.success("Ruolo creato con successo");
            navigate("/role-managment");
        } catch (error) {
            console.error("Error saving role:", error);
            setError("Errore durante il salvataggio del ruolo");
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} display="flex" justifyContent="center">
                    <CircularProgress />
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            {authContext.hasAuth("ADD_ROLES") ? (
                <>
                    <DashboardNavbar />
                    <MDBox pt={6} pb={3}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => navigate("/role-managment")} 
                            sx={{ mt: 2, alignSelf: "center", minWidth: "100px", color: "#ffffff", marginBottom: "30px" }}
                        >
                            Torna alla tabella
                        </Button>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Aggiungi Nuovo Ruolo
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Nome"
                                    value={roleData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    margin="normal"
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Descrizione"
                                    value={roleData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    margin="normal"
                                    required
                                />
                            </Box>

                            <Typography variant="h6" gutterBottom>
                                Autorizzazioni
                            </Typography>
                            
                            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Attivo</TableCell>
                                            <TableCell>Nome Autorizzazione</TableCell>
                                            <TableCell>Descrizione</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {availableAuths.map((auth) => (
                                            <TableRow key={auth.id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedAuths.includes(auth.id)}
                                                        onChange={() => handleAuthToggle(auth.id)}
                                                    />
                                                </TableCell>
                                                <TableCell>{auth.name}</TableCell>
                                                <TableCell>{auth.description}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => navigate("/role-managment")}
                                >
                                    Annulla
                                </Button>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? <CircularProgress size={24} /> : "Salva"}
                                </Button>
                            </Box>
                        </Paper>
                    </MDBox>
                </>
            ) : (
                <MDAlert color="error" dismissible>
                    <MDTypography variant="body2">
                        Non hai i permessi per accedere a questa pagina
                    </MDTypography>
                </MDAlert>
            )}
        </DashboardLayout>
    );
}

export default RoleAdd;
